import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { calculateTokenPrice, calculateSlippage } from "@/utils/bondingCurve";
import { calculateFeeAmount, calculateTotalWithFees } from "@/utils/feeCalculations";
import { checkVolatility } from "@/utils/volatilityChecks";
import { useToast } from "@/components/ui/use-toast";
import TradeButtons from "./trade/TradeButtons";
import SlippageSelector from "./trade/SlippageSelector";
import QuickAmountButtons from "./trade/QuickAmountButtons";
import CoinInfo from "./trade/CoinInfo";
import { useAccount, usePublicClient, useReadContract } from "wagmi";
import { checkAndBurnLiquidity } from "@/utils/contractUtils";
import { erc20Abi } from "@/components/create-coin/form/utils/contractConfig";

interface TradingPanelProps {
  coinId: string;
  tokenAddress?: string;
}

const TradingPanel = ({ coinId, tokenAddress }: TradingPanelProps) => {
  const { toast } = useToast();
  const [amount, setAmount] = useState<string>("");
  const [slippageTolerance, setSlippageTolerance] = useState<number>(0.01);
  const [lastTradeTimestamp, setLastTradeTimestamp] = useState<number | null>(null);
  const { address } = useAccount();
  const publicClient = usePublicClient();

  const { data: onChainSupply } = useReadContract({
    address: tokenAddress?.startsWith("0x") ? tokenAddress as `0x${string}` : undefined,
    abi: erc20Abi,
    functionName: "totalSupply",
  });
  // Use live token supply when available; retain a clearly isolated demo fallback.
  const currentSupply = onChainSupply ? Number(onChainSupply) / 1e18 : 1000000;
  const basePrice = 0.001;
  const reserveRatio = 0.5;
  
  const currentPrice = calculateTokenPrice({
    supply: currentSupply,
    reserveRatio,
    basePrice,
    volatility: 0.5,
    timeToMaturity: 1,
    riskFreeRate: 0.05
  });

  const handleTrade = async (isBuy: boolean) => {
    const tradeAmount = parseFloat(amount);
    if (isNaN(tradeAmount) || tradeAmount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid amount to trade",
        variant: "destructive"
      });
      return;
    }

    const feeAmount = calculateFeeAmount(tradeAmount);
    const totalWithFees = calculateTotalWithFees(tradeAmount);

    const targetPrice = calculateTokenPrice({
      supply: currentSupply + (isBuy ? tradeAmount : -tradeAmount),
      reserveRatio,
      basePrice,
      volatility: 0.5,
      timeToMaturity: 1,
      riskFreeRate: 0.05
    });

    const volatilityCheck = checkVolatility(currentPrice, targetPrice, lastTradeTimestamp || undefined);
    if (volatilityCheck.isVolatile) {
      toast({
        title: "Trade Protected",
        description: volatilityCheck.message,
        variant: "destructive"
      });
      return;
    }

    const { priceImpact, exceedsSlippage } = calculateSlippage(
      currentPrice,
      targetPrice,
      slippageTolerance
    );

    if (exceedsSlippage) {
      toast({
        title: "High price impact",
        description: `This trade has a price impact of ${priceImpact.toFixed(2)}%, which exceeds your slippage tolerance of ${(slippageTolerance * 100).toFixed(1)}%`,
        variant: "destructive"
      });
      return;
    }

    setLastTradeTimestamp(Date.now());
    
    // Calculate new market cap after trade
    const newMarketCap = currentSupply * currentPrice;
    
    // Check if we need to burn liquidity
    if (isBuy) {
      await checkAndBurnLiquidity(publicClient, coinId, BigInt(Math.floor(newMarketCap)));
    }

    toast({
      title: "Trade executed",
      description: `Successfully ${isBuy ? 'bought' : 'sold'} ${amount} tokens at ${targetPrice.toFixed(6)} MATIC (Fee: ${feeAmount.toFixed(6)} MATIC)`,
    });
  };

  const parsedAmount = parseFloat(amount);
  const feeAmount = !isNaN(parsedAmount) ? calculateFeeAmount(parsedAmount) : 0;
  const totalWithFees = !isNaN(parsedAmount) ? calculateTotalWithFees(parsedAmount) : 0;

  return (
    <div className="space-y-4">
      <Card className="glass-card p-4">
        <TradeButtons onTrade={handleTrade} />

        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground">Amount (MATIC)</label>
            <Input 
              type="number" 
              placeholder="0.00" 
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <div className="mt-2 space-y-1">
              <div className="text-sm text-muted-foreground">
                Current Price: {currentPrice.toFixed(6)} MATIC
              </div>
              {parsedAmount > 0 && (
                <>
                  <div className="text-sm text-muted-foreground">
                    Fee (2%): {feeAmount.toFixed(6)} MATIC
                  </div>
                  <div className="text-sm font-medium">
                    Total with fees: {totalWithFees.toFixed(6)} MATIC
                  </div>
                </>
              )}
            </div>
          </div>

          <SlippageSelector 
            slippageTolerance={slippageTolerance}
            onSlippageChange={setSlippageTolerance}
          />

          <QuickAmountButtons onAmountSelect={setAmount} />
        </div>
      </Card>

      <Card className="glass-card p-4">
        <CoinInfo coinId={coinId} tokenAddress={tokenAddress} />
      </Card>
    </div>
  );
};

export default TradingPanel;
